/*
 *  ************************************************************************
 *  *  © [2015 - 2024] Quintype Technologies India Private Limited
 *  *  All Rights Reserved.
 *  *************************************************************************
 */
import React, { useState } from 'react';
import lowerCase from 'lodash/lowerCase';
import get from 'lodash/get';
import { SvgHandler } from '../../../../svg-handler/svg-handler';
import downChevron from '../../../../../../assets/icons/down-arrow.svg';
import { getDateObj, textWithFallback } from '../../helpers';
import { Invoices } from '../invoices';
import './styles.m.css';

const FALLBACK_EXPIRED_SUB = '<p>Expired Subscription(s)</p>';
const FALLBACK_EXPIRED_ON = '<p>Expired on</p>';
const FALLBACK_PLAN_DURATION = {
  day: 'day',
  days: 'days',
  weeks: 'weeks',
  month: 'month',
  months: 'months',
  year: 'year',
  years: 'years'
};

const ExpiredPlanDisplayCard = ({
  nameStr,
  valueStr,
  durationLength,
  durationUnit,
  expiredOnTxt,
  expiryTimestamp,
  localizedMonths,
  localizedPublishTime,
  mode,
  index,
  isLastCard,
  invoices,
  subscriptionId,
  target,
  manageSubscriptionsConfig
}) => {
  const { dd: expiry_dd, mm: dispMonth, yy: expiry_yyyy } = getDateObj(expiryTimestamp, localizedMonths);
  const timeDuration =
    get(localizedPublishTime, [lowerCase(durationUnit)]) || FALLBACK_PLAN_DURATION[lowerCase(durationUnit)];
  const { dl_invoice_enabled: invoicesEnabled } = manageSubscriptionsConfig;

  return (
    <div
      styleName={`expired-subscriptions-disp-card ${mode} ${isLastCard ? 'last-card' : ''}`}
      data-test-id={`manage-sub-expired-sub-card-wrapper-${index}`}
    >
      <div styleName='expired-subscriptions-content'>
        <div styleName='expired-subscriptions-name-and-val'>
          <div data-test-id={`manage-sub-expired-sub-plan-name-${index}`}>{nameStr}</div>
          <div data-test-id={`manage-sub-expired-sub-plan-value-${index}`} styleName='expired-subscriptions-value'>
            {valueStr}
          </div>
        </div>
        <div data-test-id={`manage-sub-expired-sub-date-line-${index}`} styleName='expired-subscriptions-date-line'>
          <div>{durationLength}</div>
          <div>{timeDuration}</div>
          <div>{` | `}</div>
          <div dangerouslySetInnerHTML={{ __html: textWithFallback(expiredOnTxt, FALLBACK_EXPIRED_ON) }} />
          <div>{expiry_dd}</div>
          <div>{dispMonth}</div>
          <div>{expiry_yyyy}</div>
        </div>
      </div>
      {invoicesEnabled && (
        <div styleName={`expired-subscriptions-download-invoice`}>
          <Invoices
            invoices={invoices}
            subscriptionId={subscriptionId}
            target={target}
            invoicesConfig={manageSubscriptionsConfig}
          />
        </div>
      )}
    </div>
  );
};

export const ExpiredSubscriptions = ({
  localizationConfig,
  memberSubscriptions,
  manageSubscriptionsConfig,
  mode,
  target
}) => {
  const [showExpiredSubscriptions, setShowExpiredSubscriptions] = useState(false);

  const { expiredSubscription_label, expiredSubscription_expiredOn } = manageSubscriptionsConfig;
  const { enableLocalization = false, localizedElements = {} } = localizationConfig;
  const { months = {}, publishTime = {} } = enableLocalization ? localizedElements : {};
  const expiredSubscriptions = memberSubscriptions
    .filter((s) => s.status === 'expired' && s.subscription_type !== 'dynamic_asset')
    .sort((a, b) => new Date(b.end_timestamp) - new Date(a.end_timestamp));

  const expiredSubsDropdownHandler = () => {
    setShowExpiredSubscriptions(!showExpiredSubscriptions);
  };
  if (!expiredSubscriptions.length) return null;
  return (
    <div styleName='expired-subscriptions-wrapper'>
      <div styleName='expired-subscriptions-accordion' onClick={expiredSubsDropdownHandler}>
        <div
          data-test-id='manage-sub-expired-sub-label'
          dangerouslySetInnerHTML={{ __html: textWithFallback(expiredSubscription_label, FALLBACK_EXPIRED_SUB) }}
        ></div>
        <div
          style={{ ...(showExpiredSubscriptions ? { transform: 'rotate(180deg)' } : {}) }}
          styleName='expired-subscriptions-icon-wrapper'
          data-test-id='manage-sub-expired-sub-chevron'
        >
          <SvgHandler xlinkHref={downChevron} width='24' height='24' />
        </div>
      </div>
      {showExpiredSubscriptions && (
        <div styleName={`expired-subscriptions-cards-wrapper ${mode}`}>
          {expiredSubscriptions.map((sub, index) => {
            const {
              id,
              plan_name: planName,
              duration_length: durationLength,
              duration_unit: durationUnit,
              plan_amount_cents: amountInCents,
              plan_amount_currency: planCurrency,
              end_timestamp: expiryTimestamp,
              invoices
            } = sub;
            const amount = Number(amountInCents) / 100;
            return (
              <ExpiredPlanDisplayCard
                key={id}
                index={index}
                nameStr={planName}
                valueStr={`${planCurrency} ${amount}`}
                durationLength={durationLength}
                durationUnit={durationUnit}
                expiredOnTxt={expiredSubscription_expiredOn}
                expiryTimestamp={expiryTimestamp}
                localizedMonths={months}
                localizedPublishTime={publishTime}
                mode={mode}
                isLastCard={index === expiredSubscriptions.length - 1}
                invoices={invoices}
                subscriptionId={id}
                target={target}
                manageSubscriptionsConfig={manageSubscriptionsConfig}
              />
            );
          })}
        </div>
      )}
    </div>
  );
};
